import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Sparkles, ArrowLeft, Zap, QrCode, X, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { trackEvent } from "@/components/Analytics";
import SlackIntegrationPanel from "@/components/SlackIntegrationPanel";

async function startGoogleLogin() {
  try {
    const { data } = await api.get("/auth/google/login");
    window.location.href = data.auth_url;
  } catch (e) {
    console.error("Could not start Google login", e);
    alert("Could not start Google login. Please try again.");
  }
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoValid, setPromoValid] = useState(null); // {code, percent_off, note} or null
  const [promoError, setPromoError] = useState("");
  const [validating, setValidating] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [startingTrial, setStartingTrial] = useState(null);
  const [upiQr, setUpiQr] = useState(null); // { plan_id, plan_name, amount, checkout_url, order_id } or null
  const [upiPolling, setUpiPolling] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans));
    if (user) {
      api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
      api.get("/billing/trial-status").then(({ data }) => setTrialUsed(data.trial_used)).catch(() => {});
    }
  }, [user]);

  const startTrial = async () => {
    if (!user) { startGoogleLogin(); return; }
    setStartingTrial("no-card");
    try {
      const { data } = await api.post("/billing/start-trial");
      toast.success(`7-day Pro trial started — ends ${new Date(data.expires_at).toLocaleDateString()}`);
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
      setStartingTrial(null);
    }
  };

  const startTrialWithCard = async (tier = "pro") => {
    if (!user) { startGoogleLogin(); return; }
    setStartingTrial(tier);
    trackEvent("trial_with_card_start", { tier });
    try {
      const { data } = await api.post("/billing/start-trial-with-card", { tier });

      // Load Razorpay SDK if needed
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve;
          s.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
          document.body.appendChild(s);
        });
      }

      const rz = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "SourceDeck",
        description: `${data.plan_name} — 7-day free trial, then ₹${data.monthly_price_inr}/mo`,
        prefill: {
          name: data.user_name || user.name || "",
          email: data.user_email || user.email || "",
        },
        theme: { color: "#FF3B30" },
        handler: async (response) => {
          try {
            await api.post("/billing/verify-subscription", response);
            trackEvent("trial_with_card_activated", { tier });
            toast.success(`${data.plan_name} trial active — no charge until day 7. Cancel anytime.`);
            setTimeout(() => navigate("/dashboard"), 900);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed. Contact support.");
            setStartingTrial(null);
          }
        },
        modal: {
          ondismiss: () => setStartingTrial(null),
        },
      });
      rz.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Card auth failed. Try another card.");
        setStartingTrial(null);
      });
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial with card");
      setStartingTrial(null);
    }
  };

  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    setPromoError("");
    try {
      const { data } = await api.post("/billing/validate-promo", { code });
      setPromoValid(data);
      toast.success(`${data.percent_off}% off applied`);
    } catch (e) {
      setPromoValid(null);
      setPromoError(e?.response?.data?.detail || "Invalid code");
    } finally { setValidating(false); }
  };

  const upgrade = async (planId) => {
    if (!user) { startGoogleLogin(); return; }
    setLoadingPlan(planId);
    const plan = plans.find(p => p.id === planId);
    trackEvent("begin_checkout", { plan_id: planId, value: plan?.price, currency: "INR" });
    try {
      const body = { plan_id: planId, origin_url: window.location.origin };
      if (promoValid?.code) body.promo_code = promoValid.code;
      const { data } = await api.post("/billing/checkout", body);

      // Load Razorpay SDK (idempotent)
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve;
          s.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
          document.body.appendChild(s);
        });
      }

      const rz = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "SourceDeck",
        description: data.plan_name + (data.promo_applied ? ` · ${data.promo_applied.code} applied` : ""),
        prefill: {
          name: data.user_name || user.name || "",
          email: data.user_email || user.email || "",
        },
        theme: { color: "#FF3B30" },
        handler: async (response) => {
          try {
            await api.post("/billing/verify", response);
            trackEvent("purchase", {
              plan_id: planId,
              value: plan?.price,
              currency: "INR",
              transaction_id: response.razorpay_payment_id,
            });
            toast.success("Payment successful — welcome to " + data.plan_name);
            setTimeout(() => navigate(`/billing/success?order_id=${response.razorpay_order_id}`), 700);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed. Contact support with your payment ID.");
            setLoadingPlan(null);
          }
        },
        modal: {
          ondismiss: () => setLoadingPlan(null),
        },
        notes: { plan_id: planId },
      });
      rz.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Try again.");
        setLoadingPlan(null);
      });
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const currentTier = sub?.active ? sub.tier : "free";
  const discountedPrice = (price) => promoValid ? Math.round(price * (100 - promoValid.percent_off)) / 100 : price;
  const fmtInr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

  // Fast-path: create Razorpay order + show a QR that encodes the Razorpay hosted-checkout URL.
  // User scans on phone → opens Razorpay page → picks UPI app → pays. We poll order status.
  const payWithUpiQr = async (planId) => {
    if (!user) { startGoogleLogin(); return; }
    const plan = plans.find(p => p.id === planId);
    trackEvent("begin_checkout_upi_qr", { plan_id: planId, value: plan?.price, currency: "INR" });
    try {
      const body = { plan_id: planId, origin_url: window.location.origin };
      if (promoValid?.code) body.promo_code = promoValid.code;
      const { data } = await api.post("/billing/checkout", body);
      // Razorpay hosted checkout page — when scanned on mobile, opens Razorpay page,
      // user taps UPI, native UPI intent hands off to Google Pay / PhonePe / Paytm.
      const checkoutUrl = `https://api.razorpay.com/v1/checkout/embedded?key_id=${data.key_id}&order_id=${data.order_id}&name=SourceDeck&description=${encodeURIComponent(data.plan_name)}&prefill[email]=${encodeURIComponent(user.email)}&prefill[name]=${encodeURIComponent(user.name || "")}&theme[color]=%23FF3B30&method[upi]=true`;
      setUpiQr({
        plan_id: planId,
        plan_name: data.plan_name,
        amount: data.amount / 100, // paise → rupees
        checkout_url: checkoutUrl,
        order_id: data.order_id,
      });
      // Start polling order status — auto-close modal + redirect when captured
      setUpiPolling(true);
      pollUpiOrder(data.order_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start UPI checkout");
    }
  };

  const pollUpiOrder = async (orderId, attempts = 0) => {
    if (attempts > 60) { setUpiPolling(false); return; } // ~5 min max
    try {
      const { data } = await api.get(`/billing/order/${orderId}`);
      if (data.payment_status === "paid") {
        setUpiPolling(false);
        setUpiQr(null);
        trackEvent("purchase", { plan_id: data.plan_id, value: data.amount, currency: "INR", transaction_id: orderId, method: "upi_qr" });
        toast.success("Payment received — welcome to " + data.plan_name);
        setTimeout(() => navigate(`/billing/success?order_id=${orderId}`), 600);
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => pollUpiOrder(orderId, attempts + 1), 5000);
  };

  const closeUpiQr = () => { setUpiQr(null); setUpiPolling(false); };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain">
      <SEO
        title="Pricing — Solo ₹499 · Pro ₹999 · Studio ₹2,999 · SourceDeck"
        description="Simple monthly pricing for Indian creators. 7-day free Pro trial, no card required. Cancel anytime. Powered by Razorpay."
        path="/pricing"
      />
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/70 backdrop-blur-xl border-b border-[#27272a]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="pricing-logo">
            <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
            <span className="font-display text-xl tracking-tight">SourceDeck</span>
          </Link>
          <Link to={user ? "/dashboard" : "/"} className="sd-btn sd-btn-ghost h-9" data-testid="pricing-back-link">
            <ArrowLeft size={13} strokeWidth={1.5} /> Back
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pt-24 pb-16 sd-rise">
        <div className="sd-label mb-6"><span className="text-[#FF3B30]">●</span>&nbsp;&nbsp;PRICING</div>
        <h1 className="font-display text-[56px] md:text-[80px] leading-[0.95] tracking-tight max-w-3xl">
          Priced for creators <span className="italic text-zinc-400">who ship.</span>
        </h1>
        <p className="mt-6 text-zinc-400 max-w-xl text-lg leading-relaxed">
          One workspace, one bill, real ROI. Cancel any time — your data stays in a free export.
          {sub?.active && (
            <span className="block mt-4 font-mono text-xs text-[#34C759]">
              ● You're on <span className="uppercase">{sub.plan_name}</span> until {new Date(sub.expires_at).toLocaleDateString()}
            </span>
          )}
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-32">
        {/* Promo code */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 max-w-2xl">
          <div className="sd-label shrink-0">HAVE A CODE?</div>
          <div className="flex flex-1 gap-2 w-full">
            <input
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
              placeholder="HUNT50"
              className="sd-input h-10 flex-1 font-mono uppercase tracking-wider"
              disabled={validating || !!promoValid}
              data-testid="promo-input"
            />
            {promoValid ? (
              <button
                onClick={() => { setPromoValid(null); setPromoCode(""); }}
                className="sd-btn sd-btn-ghost h-10 shrink-0"
                data-testid="promo-remove-btn"
              >
                Remove
              </button>
            ) : (
              <button
                onClick={applyPromo}
                disabled={validating || !promoCode.trim()}
                className="sd-btn sd-btn-primary h-10 shrink-0"
                data-testid="promo-apply-btn"
              >
                {validating ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
              </button>
            )}
          </div>
        </div>
        {promoValid && (
          <div className="mb-6 text-sm text-[#34C759] font-mono" data-testid="promo-applied-msg">
            ● {promoValid.code} · {promoValid.percent_off}% OFF · {promoValid.note}
          </div>
        )}
        {promoError && (
          <div className="mb-6 text-sm text-[#FF3B30] font-mono" data-testid="promo-error-msg">
            × {promoError}
          </div>
        )}

        {/* 7-day Free Trial (no-card) CTA — kept prominent as the low-friction path */}
        {!sub?.active && !trialUsed && (
          <div className="mb-8 sd-card p-6 md:p-8 border-[#34C759]/40" data-testid="trial-cta-card">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#34C759]/[0.12] border border-[#34C759]/40">
                <Zap size={20} strokeWidth={1.5} className="text-[#34C759]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="sd-label mb-1 text-[#34C759]">● 7-DAY PRO TRIAL · NO CARD</div>
                <div className="font-display text-2xl md:text-3xl leading-tight">
                  Try Pro free for 7 days
                </div>
                <div className="mt-2 text-sm text-zinc-400">
                  Full Pro access — unlimited AI · unlimited projects · live analytics · Fit Score. Auto-drops to Free when it ends, nothing to cancel.
                </div>
              </div>
              <button
                onClick={startTrial}
                disabled={startingTrial}
                className="sd-btn sd-btn-accent h-12 px-5 shrink-0"
                data-testid="start-trial-btn"
              >
                {startingTrial === "no-card" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
                Start free trial <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>
            <p className="mt-4 text-[11px] text-zinc-500 font-mono">
              Want to trial a specific plan (Solo, Pro, or Studio) with auto-renew? Use the "Start 7-day trial" button on each plan card below — ₹0 today, then that plan's price on day 7.
            </p>
          </div>
        )}
        {trialUsed && !sub?.active && (
          <div className="mb-8 border border-[#27272a] p-4 text-xs font-mono text-zinc-500" data-testid="trial-used-msg">
            ● Trial already used. Pick a plan below to continue.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const isCurrent = currentTier === p.id;
            const isFeatured = p.id === "pro";
            // Rank tiers so we know if a plan is a downgrade from current
            const tierRank = { free: 0, solo: 1, pro: 2, studio: 3 };
            const isDowngrade = !isCurrent && tierRank[p.id] < tierRank[currentTier];
            return (
              <div
                key={p.id}
                className={`sd-card p-8 flex flex-col ${isFeatured ? "border-[#FF3B30]" : ""} ${isDowngrade ? "opacity-50" : ""}`}
                data-testid={`pricing-card-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="sd-label">{p.name.toUpperCase()}</div>
                  {isFeatured && !isCurrent && <div className="sd-label text-[#FF3B30]">MOST POPULAR</div>}
                  {isCurrent && <div className="sd-label text-[#34C759]">● ACTIVE</div>}
                  {isDowngrade && <div className="sd-label text-zinc-500">LOWER TIER</div>}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  {promoValid && !isDowngrade && (
                    <span className="font-mono text-lg text-zinc-500 line-through">{fmtInr(p.price)}</span>
                  )}
                  <span className="font-display text-6xl" style={{ color: promoValid && !isDowngrade ? "#34C759" : undefined }}>
                    {fmtInr(isDowngrade ? p.price : discountedPrice(p.price))}
                  </span>
                  <span className="text-zinc-500 font-mono text-sm">/mo</span>
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  {promoValid && !isDowngrade ? `${promoValid.percent_off}% off applied · ` : ""}Renews monthly · cancel anytime
                </p>

                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check size={14} strokeWidth={1.5} className={isFeatured ? "text-[#FF3B30] mt-0.5" : "text-white mt-0.5"} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => upgrade(p.id)}
                  disabled={loadingPlan === p.id || isCurrent || isDowngrade}
                  className={`sd-btn h-12 mt-8 ${isFeatured ? "sd-btn-accent" : "sd-btn-primary"} ${(isCurrent || isDowngrade) ? "opacity-70 cursor-not-allowed" : ""}`}
                  data-testid={`pricing-cta-${p.id}`}
                  title={isDowngrade ? `You're already on ${sub?.plan_name || currentTier.toUpperCase()} — a higher plan. Contact support to downgrade.` : undefined}
                >
                  {loadingPlan === p.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : isCurrent ? "Current plan" : isDowngrade ? (
                    "Not available on your plan"
                  ) : (
                    <>Upgrade to {p.name} <ArrowRight size={14} strokeWidth={1.5} /></>
                  )}
                </button>
                {!isCurrent && !isDowngrade && !trialUsed && !sub?.active && (
                  <button
                    onClick={() => startTrialWithCard(p.id)}
                    disabled={startingTrial === p.id}
                    className="sd-btn sd-btn-ghost h-10 mt-2 text-xs w-full border border-[#34C759]/40 text-[#34C759] hover:bg-[#34C759]/[0.08]"
                    data-testid={`pricing-trial-${p.id}`}
                    title={`Start a 7-day free trial of ${p.name}. ₹0 today, ${fmtInr(p.price)} auto-charged on day 7. Cancel anytime.`}
                  >
                    {startingTrial === p.id ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} strokeWidth={1.5} />}
                    Start 7-day free trial · {fmtInr(p.price)}/mo after
                  </button>
                )}
                {!isCurrent && !isDowngrade && (
                  <button
                    onClick={() => payWithUpiQr(p.id)}
                    disabled={loadingPlan === p.id}
                    className="sd-btn sd-btn-ghost h-10 mt-2 text-xs w-full"
                    data-testid={`pricing-upi-qr-${p.id}`}
                    title="Scan QR on your phone, pay with any UPI app (Google Pay, PhonePe, Paytm)"
                  >
                    <QrCode size={12} strokeWidth={1.5} /> Pay via UPI QR (10 sec)
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-14 sd-card p-8 flex flex-col md:flex-row items-start md:items-center gap-4">
          <Sparkles size={22} strokeWidth={1.25} className="text-[#FF3B30]" />
          <div className="flex-1">
            <div className="sd-label mb-2">AI, INCLUDED</div>
            <div className="font-display text-2xl">Claude Sonnet 4.5 powers every AI feature. Studio gets Claude Opus.</div>
            <div className="text-sm text-zinc-500 mt-1">No hidden per-token fees on Solo/Pro/Studio. Fair-use limits shown above.</div>
          </div>
        </div>

        {/* Studio-tier integrations */}
        <div className="mt-6" id="studio">
          <SlackIntegrationPanel userTier={sub?.tier || "free"} />
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm text-zinc-400">
          <div className="border border-[#27272a] p-6">
            <div className="sd-label mb-2">CANCEL ANYTIME</div>
            No annual contracts. Downgrade or cancel from your billing page in one click.
          </div>
          <div className="border border-[#27272a] p-6">
            <div className="sd-label mb-2">YOUR DATA IS YOURS</div>
            Export scripts, sponsor data, and analytics as JSON / CSV at any time.
          </div>
          <div className="border border-[#27272a] p-6">
            <div className="sd-label mb-2">SECURE PAYMENTS</div>
            Powered by Razorpay · UPI, cards, netbanking, and wallets. Card details never touch our servers.
          </div>
        </div>
      </section>

      {/* UPI QR Modal */}
      {upiQr && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={closeUpiQr}
          data-testid="upi-qr-modal"
        >
          <div
            className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-md flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-[#27272a] flex items-start justify-between">
              <div>
                <div className="sd-label mb-2 text-[#34C759]">● SCAN &amp; PAY</div>
                <div className="font-display text-2xl">{upiQr.plan_name}</div>
                <div className="text-3xl font-display mt-1">{fmtInr(upiQr.amount)}</div>
              </div>
              <button
                onClick={closeUpiQr}
                className="text-zinc-500 hover:text-white"
                data-testid="upi-qr-close-btn"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-8 flex flex-col items-center">
              <div className="p-4 bg-white" data-testid="upi-qr-code">
                <QRCodeSVG
                  value={upiQr.checkout_url}
                  size={220}
                  bgColor="#FFFFFF"
                  fgColor="#0a0a0a"
                  level="M"
                  includeMargin={false}
                />
              </div>

              <div className="mt-6 space-y-3 w-full">
                <div className="flex items-start gap-3">
                  <div className="font-mono text-xs text-[#FF3B30] mt-0.5">01</div>
                  <div className="text-sm text-zinc-300 flex-1">
                    Open Google Pay, PhonePe, or Paytm on your phone.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="font-mono text-xs text-[#FF3B30] mt-0.5">02</div>
                  <div className="text-sm text-zinc-300 flex-1">
                    Tap Scan QR → scan the code above.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="font-mono text-xs text-[#FF3B30] mt-0.5">03</div>
                  <div className="text-sm text-zinc-300 flex-1">
                    Confirm payment. This page auto-updates when we receive it.
                  </div>
                </div>
              </div>

              {upiPolling && (
                <div className="mt-6 flex items-center gap-2 font-mono text-xs text-zinc-500" data-testid="upi-qr-waiting">
                  <Loader2 size={12} className="animate-spin text-[#FF3B30]" />
                  Waiting for your payment…
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#27272a] text-center">
              <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider flex items-center justify-center gap-2">
                <Smartphone size={11} strokeWidth={1.5} />
                On mobile? <a href={upiQr.checkout_url} target="_blank" rel="noreferrer" className="text-[#FF3B30] hover:underline ml-1" data-testid="upi-qr-mobile-link">Open Razorpay directly →</a>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
